/**
 * Bay detail screen (DNKADM-0054 U2; cards DNKADM-0495) — one `v2_resource`, routed at
 * `/resources/:resourceId` under `RequireAdmin`.
 *
 * **Read-only cards, one Edit each (DNKADM-0495, the DNKADM-0493 pattern).** The page is
 * three summary cards — Basics, Hours, Days closed — and each card's single Edit opens a
 * right-side drawer with one Save (or Done). Nothing outside a drawer saves. The header
 * holds the name, the status chip and a More menu (Turn off / Turn on: the same
 * deactivate / reactivate calls the list makes, with the same plan-limit handling).
 *
 * - **Basics** — name, capacity, description, locations, status. The drawer is the existing
 *   {@link ResourceDialog} form in drawer mode: same fields, same PUTs (the resource, then
 *   the location assignments when they changed).
 * - **Hours** — the weekly schedule for each location the bay is at, read with the same
 *   `GET …/schedule?locationId=` the old location tabs made (now one per location up front).
 *   The drawer hosts the existing {@link ResourceScheduleGrid} (embedded: the drawer's Save
 *   sends the same whole-week PUT for the picked location). A bay at no location collapses
 *   to one line, its Edit held off until a location is added in Basics.
 * - **Days closed** — the date exceptions. The drawer hosts the existing
 *   {@link ResourceDateExceptionsPanel} unchanged: it adds and removes one date at a time
 *   (its own POST / DELETE), so the drawer's footer is Done.
 *
 * The word for the bay is the location's own word when every assigned location agrees on
 * one, else "bay" ({@link bayNoun}). A load failure shows error + Retry; a missing
 * resource (`404`) shows a not-found note with a link back to the list. A failed or pending
 * card read shows "Couldn't load" or "—", never a false "none".
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  Link as MuiLink,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@/theme';
import { IconAction } from '@/components/IconAction';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { serverMessage } from '@/api/serverMessage';
import { PlanLimitDialog } from '@/components/PlanLimitDialog';
import { parsePlanLimit, v2ErrorCode, type PlanLimitInfo } from '@/api/v2Errors';
import {
  deactivateResource,
  getResource,
  getResourceLocations,
  getResourceSchedule,
  listResourceDateExceptions,
  reactivateResource,
  type ResourceDateException,
  type ResourceScheduleDay,
  type V2Resource,
} from '@/api/resources';
import { listLocations, type V2Location } from '@/api/locations';
import { dateLabel } from '@/utils/clockTime';
import { SummaryCard, SummaryRow } from '@/features/services/SummaryCard';
import { EditDrawer } from '@/features/services/EditDrawer';
import { shiftsLabel, weekLines, hasOpenDay } from '@/features/operating-hours/hoursSummary';
import { ResourceScheduleGrid, type ResourceScheduleGridHandle } from './ResourceScheduleGrid';
import { ResourceDateExceptionsPanel } from './ResourceDateExceptionsPanel';
import { ResourceDialog } from './ResourceDialog';
import { bayNoun, capitalize } from './bayNoun';
import { ScreenError } from '@/components/ScreenError';

/** Display name for a resource (its name, falling back to the id). */
function resourceName(resource: V2Resource): string {
  return resource.name.length > 0 ? resource.name : resource.id;
}

function locationName(loc: V2Location): string {
  return loc.name === '' ? loc.id : loc.name;
}

/** One location's weekly schedule, or `null` days when its read failed. */
interface LocationSchedule {
  location: V2Location;
  days: ResourceScheduleDay[] | null;
}

type CardDrawer = 'basics' | 'hours' | 'closed';

export function ResourceDetail() {
  const { resourceId = '' } = useParams<{ resourceId: string }>();
  const showToast = useToast();

  const resource = useResource(
    useCallback(() => getResource(resourceId), [resourceId]),
    [resourceId],
  );

  // Resolve the resource's assigned locations to full V2Location rows (names, the
  // exception location options, and the location's word for a bay).
  const assigned = useResource(
    useCallback(async (): Promise<V2Location[]> => {
      const [ids, all] = await Promise.all([
        getResourceLocations(resourceId),
        listLocations({ status: 'active' }),
      ]);
      const order = new Map(all.map((l, i) => [l.id, i] as const));
      return all
        .filter((l) => ids.includes(l.id))
        .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    }, [resourceId]),
    [resourceId],
  );
  const assignedData = assigned.data;
  const assignedLocations = useMemo(() => assignedData ?? [], [assignedData]);
  const assignedKey = assignedLocations.map((l) => l.id).join('|');

  // Each assigned location's weekly schedule — the same GET the old location tab made,
  // now one per location so the Hours card can show them all. Best-effort per location.
  // `assignedKey` stands for the location list: the reads re-run only when it changes.
  const schedules = useResource(
    () =>
      Promise.all(
        assignedLocations.map(async (location): Promise<LocationSchedule> => {
          try {
            const schedule = await getResourceSchedule(resourceId, location.id);
            return { location, days: schedule.days };
          } catch {
            return { location, days: null };
          }
        }),
      ),
    [resourceId, assignedKey],
  );

  const exceptions = useResource(
    useCallback(() => listResourceDateExceptions(resourceId), [resourceId]),
    [resourceId],
  );

  const noun = bayNoun(assignedLocations);
  const Noun = capitalize(noun);

  // ---- Card drawers ----
  const [drawer, setDrawer] = useState<CardDrawer | null>(null);
  const scheduleRef = useRef<ResourceScheduleGridHandle>(null);
  const [hoursLocationId, setHoursLocationId] = useState<string | null>(null);
  const closeDrawer = () => setDrawer(null);

  // ---- Header actions (More menu) ----
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [planLimit, setPlanLimit] = useState<PlanLimitInfo | null>(null);

  async function handleTurnOff(res: V2Resource) {
    setMenuAnchor(null);
    try {
      await deactivateResource(res.id);
      resource.refetch();
      showToast(`${Noun} turned off`, 'success');
    } catch (err) {
      showToast(serverMessage(err) ?? `Couldn’t turn the ${noun} off.`, 'error');
    }
  }

  async function handleTurnOn(res: V2Resource) {
    setMenuAnchor(null);
    try {
      await reactivateResource(res.id);
      resource.refetch();
      showToast(`${Noun} turned on`, 'success');
    } catch (err) {
      // Reactivate consumes a plan slot, so it is plan-limit enforced exactly as on the
      // list (DNKADM-0065 / DNKAPI-0139).
      const code = v2ErrorCode(err);
      if (code === 'PLAN_LIMIT_REACHED') {
        setPlanLimit(parsePlanLimit(err));
        return;
      }
      if (code === 'PLAN_VERIFY_FAILED') {
        showToast('We couldn’t verify your plan just now. Please try again in a moment.', 'error');
        return;
      }
      showToast(serverMessage(err) ?? `Couldn’t turn the ${noun} on.`, 'error');
    }
  }

  const res = resource.data;
  const hoursLocation =
    assignedLocations.find((l) => l.id === hoursLocationId) ?? assignedLocations[0] ?? null;
  const hoursSchedule =
    hoursLocation === null
      ? null
      : (schedules.data?.find((s) => s.location.id === hoursLocation.id) ?? null);

  return (
    <Box>
      <MuiLink
        component={RouterLink}
        to="/resources"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13, mb: 2 }}
      >
        <Icon name="arrow_back" sx={{ fontSize: 18 }} />
        All resources
      </MuiLink>

      {resource.status === 'loading' && !res && (
        <Skeleton
          variant="rounded"
          height={120}
          role="status"
          aria-label="Loading resource"
          sx={{ borderRadius: 3, maxWidth: 560 }}
        />
      )}

      {resource.status === 'error' && (
        <ScreenError
          error={resource.error}
          what="the resource"
          onRetry={resource.refetch}
          sx={{ maxWidth: 560 }}
          notFound={
            <Alert severity="warning" sx={{ maxWidth: 560 }}>
              That resource wasn’t found. It may have been removed.{' '}
              <MuiLink component={RouterLink} to="/resources">
                Back to resources
              </MuiLink>
              .
            </Alert>
          }
        />
      )}

      {/* While the page re-reads after a drawer saves, the last values stay on screen. */}
      {resource.status !== 'error' && res && (
        <>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Typography variant="h6" component="h1">
              {resourceName(res)}
            </Typography>
            <Chip
              size="small"
              label={res.isActive ? 'Active' : 'Inactive'}
              color={res.isActive ? 'success' : 'default'}
              variant={res.isActive ? 'filled' : 'outlined'}
            />
            <Box sx={{ flex: 1 }} />
            <IconAction label="More actions" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <Icon name="more_vert" />
            </IconAction>
            <Menu
              anchorEl={menuAnchor}
              open={menuAnchor !== null}
              onClose={() => setMenuAnchor(null)}
            >
              {res.isActive ? (
                <MenuItem onClick={() => void handleTurnOff(res)}>Turn off</MenuItem>
              ) : (
                <MenuItem onClick={() => void handleTurnOn(res)}>Turn on</MenuItem>
              )}
            </Menu>
          </Stack>

          <Stack spacing={2} sx={{ maxWidth: 880, mb: 4 }}>
            {/* ---- Basics ---- */}
            <SummaryCard title="Basics" onEdit={() => setDrawer('basics')}>
              <SummaryRow label="Name" value={resourceName(res)} />
              <SummaryRow
                label="Bookings at once"
                value={`${res.capacity} at a time`}
                hint={`How many bookings this ${noun} can take at the same time.`}
              />
              <SummaryRow label="Description" value={res.description ?? 'None'} />
              <SummaryRow
                label="Locations"
                value={
                  assigned.status === 'error'
                    ? 'Couldn’t load'
                    : assignedData === null
                      ? '—'
                      : assignedLocations.length === 0
                        ? 'Not at any location yet'
                        : assignedLocations.map(locationName).join(', ')
                }
              />
              <SummaryRow
                label="Status"
                value={res.isActive ? 'Active' : 'Inactive'}
                hint="Change it from More."
                last
              />
            </SummaryCard>

            {/* ---- Hours ---- */}
            <HoursCard
              noun={noun}
              assignedStatus={assignedData === null ? assigned.status : 'ready'}
              locations={assignedLocations}
              schedules={schedules.data}
              schedulesFailed={schedules.status === 'error'}
              onEdit={() => {
                setHoursLocationId(null);
                setDrawer('hours');
              }}
            />

            {/* ---- Days closed ---- */}
            <DaysClosedCard
              exceptions={exceptions.data}
              failed={exceptions.status === 'error' && exceptions.data === null}
              locations={assignedLocations}
              onEdit={() => setDrawer('closed')}
            />
          </Stack>

          {/* ---- Drawers ---- */}
          <ResourceDialog
            open={drawer === 'basics'}
            drawer
            resource={res}
            onClose={closeDrawer}
            onSaved={() => {
              closeDrawer();
              resource.refetch();
              assigned.refetch();
            }}
            showToast={showToast}
          />

          <EditDrawer
            open={drawer === 'hours'}
            title="Edit hours"
            onClose={closeDrawer}
            saveDisabled={hoursSchedule === null || hoursSchedule.days === null}
            onSave={async () => {
              const ok = (await scheduleRef.current?.save()) ?? false;
              if (ok) showToast('Changes saved', 'success');
              return ok;
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              The weekly hours this {noun} can be booked, in the location’s local time.
            </Typography>
            {assignedLocations.length > 1 && (
              <TextField
                select
                label="Location"
                size="small"
                value={hoursLocation?.id ?? ''}
                onChange={(e) => setHoursLocationId(e.target.value)}
                sx={{ mb: 2, minWidth: 240 }}
                slotProps={{ htmlInput: { 'aria-label': 'Hours location' } }}
              >
                {assignedLocations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {locationName(loc)}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {hoursLocation !== null && hoursSchedule === null && (
              <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} />
            )}
            {hoursSchedule !== null && hoursSchedule.days === null && (
              <Alert severity="error">
                Couldn’t load the hours for this location. Close this and try again.
              </Alert>
            )}
            {hoursLocation !== null && hoursSchedule !== null && hoursSchedule.days !== null && (
              <ResourceScheduleGrid
                // Remount per location so the rows reseed.
                key={`sched|${hoursLocation.id}`}
                embedded
                handleRef={scheduleRef}
                days={hoursSchedule.days}
                resourceId={resourceId}
                locationId={hoursLocation.id}
                refetch={schedules.refetch}
                showToast={showToast}
              />
            )}
          </EditDrawer>

          <EditDrawer
            open={drawer === 'closed'}
            title="Edit days closed"
            onClose={closeDrawer}
            doneOnly
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Mark this {noun} off on a specific date, or set special hours, across all locations or
              one. Each date is added or removed right away.
            </Typography>
            {exceptions.data !== null && (
              <ResourceDateExceptionsPanel
                exceptions={exceptions.data}
                resourceId={resourceId}
                locations={assignedLocations}
                refetch={exceptions.refetch}
                showToast={showToast}
              />
            )}
          </EditDrawer>

          <PlanLimitDialog
            open={planLimit !== null}
            info={planLimit}
            onClose={() => setPlanLimit(null)}
          />
        </>
      )}
    </Box>
  );
}

/** The Hours card: each location's week, or one line when there is nothing to show. */
function HoursCard({
  noun,
  assignedStatus,
  locations,
  schedules,
  schedulesFailed,
  onEdit,
}: {
  noun: string;
  assignedStatus: 'loading' | 'ready' | 'error';
  locations: V2Location[];
  schedules: LocationSchedule[] | null;
  schedulesFailed: boolean;
  onEdit: () => void;
}) {
  if (assignedStatus === 'ready' && locations.length === 0) {
    return (
      <SummaryCard
        title="Hours"
        onEdit={onEdit}
        editDisabled
        empty={`This ${noun} isn’t at any location yet. Add one in Basics first.`}
      />
    );
  }
  // Loaded = the schedules read matches the current location list (never a stale or
  // pre-load empty list read as "no hours").
  const loaded =
    assignedStatus === 'ready' &&
    schedules !== null &&
    schedules.map((s) => s.location.id).join('|') === locations.map((l) => l.id).join('|');
  if (loaded && schedules.every((s) => s.days !== null && !hasOpenDay(s.days))) {
    return <SummaryCard title="Hours" onEdit={onEdit} empty="No hours set yet" />;
  }
  const many = locations.length > 1;
  return (
    <SummaryCard title="Hours" onEdit={onEdit} editDisabled={!loaded}>
      {assignedStatus === 'error' || schedulesFailed ? (
        <SummaryRow label="Hours" value="Couldn’t load" last />
      ) : !loaded ? (
        <SummaryRow label="Hours" value="—" last />
      ) : (
        schedules.map((s, i) => {
          const lastLocation = i === schedules.length - 1;
          if (s.days === null) {
            return (
              <SummaryRow
                key={s.location.id}
                label={locationName(s.location)}
                value="Couldn’t load"
                last={lastLocation}
              />
            );
          }
          const lines = weekLines(s.days);
          return (
            <Box key={s.location.id}>
              {many && (
                <Typography variant="body2" sx={{ fontWeight: 600, mt: i === 0 ? 0 : 1.5 }}>
                  {locationName(s.location)}
                </Typography>
              )}
              {lines.map((line, j) => (
                <SummaryRow
                  key={line.days}
                  label={line.days}
                  value={line.hours}
                  last={lastLocation && j === lines.length - 1}
                />
              ))}
            </Box>
          );
        })
      )}
    </SummaryCard>
  );
}

/** The Days closed card: each date exception, or one line when there are none. */
function DaysClosedCard({
  exceptions,
  failed,
  locations,
  onEdit,
}: {
  exceptions: ResourceDateException[] | null;
  failed: boolean;
  locations: V2Location[];
  onEdit: () => void;
}) {
  if (exceptions !== null && exceptions.length === 0) {
    return <SummaryCard title="Days closed" onEdit={onEdit} empty="No days closed" />;
  }
  const scopeLabel = (ex: ResourceDateException): string => {
    if (ex.scope !== 'LOCATION') return 'All locations';
    const loc = locations.find((l) => l.id === ex.locationId);
    return loc ? locationName(loc) : (ex.locationId ?? 'One location');
  };
  return (
    <SummaryCard title="Days closed" onEdit={onEdit} editDisabled={exceptions === null}>
      {exceptions === null ? (
        <SummaryRow label="Dates" value={failed ? 'Couldn’t load' : '—'} last />
      ) : (
        exceptions.map((ex, i) => (
          <SummaryRow
            key={ex.id ?? `${ex.exceptionDate}-${i}`}
            label={dateLabel(ex.exceptionDate) ?? ex.exceptionDate}
            value={`${ex.isAvailable ? shiftsLabel(ex.shifts) : 'Closed'} · ${scopeLabel(ex)}`}
            last={i === exceptions.length - 1}
          />
        ))
      )}
    </SummaryCard>
  );
}
